import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtTime, timeAgo } from "@/lib/format";
import { Check, Utensils, Bike, PackageOpen, MonitorPlay } from "lucide-react";

const TYPE_ICON = { "dine-in": Utensils, takeaway: PackageOpen, delivery: Bike, online: Bike };

export default function KDS() {
  const qc = useQueryClient();
  const { data: orders = [] } = useQuery({
    queryKey: ["kds"],
    queryFn: async () => (await api.get("/orders")).data,
    refetchInterval: 5000,
  });

  const active = orders.filter((o) => ["pending", "preparing", "ready"].includes(o.status));

  const toggleItem = async (o, idx, ready) => {
    await api.post(`/orders/${o.id}/item-ready`, { index: idx, ready });
    qc.invalidateQueries({ queryKey: ["kds"] });
  };
  const bump = async (o) => {
    await api.put(`/orders/${o.id}`, { status: "completed" });
    toast.success(`${o.order_number} bumped`);
    qc.invalidateQueries({ queryKey: ["kds"] });
  };

  const ageColor = (iso) => {
    const mins = (Date.now() - new Date(iso).getTime()) / 60000;
    if (mins > 15) return "#FF3B30";
    if (mins > 8) return "#FFCC00";
    return "#34C759";
  };

  return (
    <div className="h-full overflow-y-auto bg-black p-5" data-testid="kds-screen">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2 text-white">
          <MonitorPlay className="w-6 h-6 text-[var(--kul-primary)]" />
          <h1 className="text-2xl font-700 tracking-tight">Kitchen Display</h1>
          <span className="text-[#8e8e93] text-sm ml-2">{active.length} active tickets</span>
        </div>
        <div className="text-[#8e8e93] text-sm">Auto-refreshing · live</div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
        {active.map((o) => {
          const Icon = TYPE_ICON[o.order_type] || Utensils;
          return (
            <div key={o.id} className="rounded-2xl p-4 flex flex-col" data-testid={`kds-ticket-${o.id}`}
              style={{ background: "#1c1c1e", boxShadow: `0 0 0 1.5px ${ageColor(o.created_at)}` }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-white">
                  <Icon className="w-4 h-4 text-[var(--kul-primary)]" />
                  <span className="font-mono-k font-semibold">{o.order_number}</span>
                </div>
                <span className="text-xs px-2 py-0.5 rounded-full text-black font-semibold capitalize" style={{ background: ageColor(o.created_at) }}>{timeAgo(o.created_at)}</span>
              </div>
              <div className="text-[11px] text-[#8e8e93] mt-1 capitalize">
                {o.order_type}{o.channel && o.channel !== "pos" ? ` · ${o.channel}` : ""} · {fmtTime(o.created_at)}
              </div>
              <div className="mt-3 space-y-1.5 flex-1">
                {o.items.map((it, i) => (
                  <button key={i} onClick={() => toggleItem(o, i, !it.ready)} data-testid={`kds-item-${o.id}-${i}`}
                    className="w-full flex items-center gap-2 text-left">
                    <span className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 ${it.ready ? "bg-[#34C759] border-[#34C759]" : "border-[#48484a]"}`}>
                      {it.ready && <Check className="w-3.5 h-3.5 text-white" />}
                    </span>
                    <span className={`text-[15px] font-medium ${it.ready ? "text-[#636366] line-through" : "text-white"}`}>{it.qty}× {it.name}</span>
                  </button>
                ))}
              </div>
              {o.notes && <div className="text-xs text-[#FFCC00] mt-2">Note: {o.notes}</div>}
              <button onClick={() => bump(o)} data-testid={`kds-bump-${o.id}`}
                className="mt-3 w-full py-2.5 rounded-xl bg-[var(--kul-primary)] text-white font-semibold active:scale-[0.97] transition-transform">
                Bump / Done
              </button>
            </div>
          );
        })}
      </div>
      {!active.length && <div className="text-center text-[#636366] py-32 text-lg">No active tickets. Kitchen is all caught up 🎉</div>}
    </div>
  );
}
